/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useEffect, useState, type ComponentProps } from 'react';
import { Link } from 'react-router';
import classnames from 'classnames';
import Picture from 'components/Picture/Picture';
import useWindowWidth from 'effects/useWindowWidth';
import useEnhanced from 'effects/enhanced';
import Nav from '../Nav/Nav';
import useAnimation from './animation';
import './Index.scss';

const DEFAULT_NAV: NonNullable<IndexProps['nav']> = [];
const DEFAULT_SPONSORS: Sponsor[] = [];

const DEFAULT_LABELS = {
  name: 'Name',
  menu: 'Menu',
  open: 'Open',
  close: 'Close',
};

const DEFAULT_ON_MOUNT = () => {};
const DEFAULT_ON_THEME = () => {};
const DEFAULT_ON_NAVIGATE = () => {};

const titleSizes = [
  'default',
  'xlarge',
  'large',
  'medium',
  'small',
  'tiny',
  'midget',
];

interface Logo {
  src: string;
  width: number;
  height: number;
}

interface Sponsor extends Omit<
  ComponentProps<'a'>,
  'id' | 'title' | 'href' | 'key' | 'children'
> {
  id: string | number;
  title: string;
  url?: string;
  logo: Logo;
}

interface IndexProps {
  title: string;
  layout?: 'small-image' | 'large-image';
  theme?: string;
  picture?: ComponentProps<typeof Picture>;
  labels?: { name: string; menu: string; open: string; close: string };
  nav?: ComponentProps<typeof Nav>['items'];
  sponsors?: Sponsor[];
  onMount?: () => void;
  onTheme?: (theme: string) => void;
  onNavigate?: ComponentProps<typeof Nav>['onClick'];
}

/**
 * This is the Index component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Index({
  title,
  layout = 'small-image',
  theme = 'white',
  picture,
  labels = DEFAULT_LABELS,
  nav = DEFAULT_NAV,
  sponsors = DEFAULT_SPONSORS,
  onMount = DEFAULT_ON_MOUNT,
  onTheme = DEFAULT_ON_THEME,
  onNavigate = DEFAULT_ON_NAVIGATE,
}: IndexProps) {
  const [ref] = useAnimation();
  const titleRef = useRef<HTMLSpanElement>(null);
  const enhanced = useEnhanced(890);
  const windowWidth = useWindowWidth();
  const [titleSize, setTitleSize] = useState<number | null>(null);

  useEffect(() => {
    onMount();
  }, []);

  useEffect(() => {
    document.body.classList.add(layout);

    const timeout = setTimeout(() => {
      onTheme(theme);
    }, 120);

    return () => {
      clearTimeout(timeout);
      document.body.classList.remove(layout);
    };
  }, [layout]);

  useEffect(() => {
    setTitleSize(null);
  }, [windowWidth]);

  useEffect(() => {
    const current = titleSize ?? 0;
    if (titleRef.current) {
      if (titleSizes[current + 1]) {
        if (
          titleRef.current.offsetWidth >
            (titleRef.current.parentNode as HTMLElement).offsetWidth ||
          (enhanced &&
            titleRef.current.offsetHeight > ref.current!.offsetHeight * 0.45) ||
          (!enhanced &&
            titleRef.current.offsetHeight > ref.current!.offsetHeight * 0.2)
        ) {
          setTitleSize(size => (size ?? 0) + 1);
        } else if (titleSize === null) {
          setTitleSize(0);
        }
      }
    }
  }, [titleSize]);

  function renderNav() {
    if (enhanced) {
      return (
        <nav className="menu">
          {nav.map(({ id, text, url }) => {
            if (url) {
              return (
                <Link
                  key={id}
                  to={url}
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              );
            }

            return <span key={id}>{text}</span>;
          })}
        </nav>
      );
    }

    return <Nav items={nav} labels={labels} onClick={onNavigate} />;
  }

  function renderLogo({ src, width, height }: Logo) {
    let renderWidth = width;
    let renderHeight = Math.round((height / width) * renderWidth);

    if (renderHeight > 40) {
      renderHeight = 40;
      renderWidth = Math.round((width / height) * renderHeight);
    }

    return (
      <div
        className="svg"
        dangerouslySetInnerHTML={{ __html: src }}
        style={{ width: renderWidth, aspectRatio: `${width} / ${height}` }}
      />
    );
  }

  function renderSponsors() {
    if (!sponsors.length) return null;

    return (
      <>
        <div className="sponsor-background" />
        <div className="sponsors">
          {sponsors.map(({ id, title, url, logo, ...props }) => (
            <a
              key={id}
              href={url}
              title={title}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {renderLogo(logo)}
            </a>
          ))}
        </div>
      </>
    );
  }

  return (
    <main ref={ref} className="page index">
      {renderNav()}
      <strong className="name">
        <span>{labels.name}</span>
      </strong>
      <h1>
        <span
          ref={titleRef}
          className={classnames(
            'lined-text lined-title',
            titleSizes[titleSize as number] ?? null
          )}
          dangerouslySetInnerHTML={{ __html: title }}
        />
      </h1>
      <div className="image">
        <Picture {...picture} />
      </div>
      {renderSponsors()}
    </main>
  );
}

export default Index;
